import React, { useState } from "react";
import { Upload, Button, message, List, Space, Tag, Modal } from "antd";
import {
  UploadOutlined,
  FileOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios.config";
import Swal from "sweetalert2";
import logger from "../../utils/logger";

const { Dragger } = Upload;

const DocumentUpload = ({
  caseId,
  clientId,
  onDocumentUploaded,
  maxCount = 5,
  acceptedTypes = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip,.rar",
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // جلب المستندات الحالية
  const fetchDocuments = async () => {
    try {
      const endpoint = caseId
        ? `/cases/${caseId}/documents`
        : clientId
        ? `/documents/clients/${clientId}`
        : null;

      if (!endpoint) return;

      const response = await axiosInstance.get(endpoint);
      if (response.data && response.data.data) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      logger.error("Error fetching documents:", error);
    }
  };

  React.useEffect(() => {
    fetchDocuments();
  }, [caseId, clientId]);

  // معالجة تغيير قائمة الملفات
  const handleChange = ({ fileList }) => {
    setFileList(fileList.slice(-maxCount)); // الحد الأقصى لعدد الملفات
  };

  // إعداد خصائص الرفع
  const uploadProps = {
    name: "file",
    multiple: true,
    fileList,
    onChange: handleChange,
    beforeUpload: () => false, // منع الرفع التلقائي
    accept: acceptedTypes,
  };

  // رفع الملفات
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("الرجاء اختيار ملف واحد على الأقل");
      return;
    }

    const formData = new FormData();

    fileList.forEach((file) => {
      formData.append("files", file.originFileObj);
    });

    // إضافة البيانات الإضافية حسب نوع المستند
    if (caseId) {
      formData.append("caseId", caseId);
    }

    if (clientId) {
      formData.append("client_id", clientId);
    }

    setUploading(true);
    try {
      const endpoint = caseId
        ? `/cases/${caseId}/documents`
        : clientId
        ? `/documents/clients/${clientId}/documents`
        : null;

      if (!endpoint) throw new Error("لم يتم تحديد القضية أو العميل");

      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("تم رفع المستندات بنجاح");
      setFileList([]);

      // جلب المستندات المحدثة
      await fetchDocuments();

      // استدعاء دالة رد الاتصال إذا كانت موجودة
      if (typeof onDocumentUploaded === "function") {
        onDocumentUploaded(response.data);
      }
    } catch (error) {
      logger.error("Upload error:", error);
      message.error(error.response?.data?.message || "فشل في رفع المستندات");
    } finally {
      setUploading(false);
    }
  };

  // حذف مستند
  const handleDeleteDocument = async (documentId) => {
    Swal.fire({
      title: "تأكيد الحذف",
      text: "هل أنت متأكد من حذف هذا المستند؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "حذف",
      cancelButtonText: "إلغاء",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/documents/${documentId}`);
          message.success("تم حذف المستند بنجاح");
          fetchDocuments(); // تحديث قائمة المستندات
        } catch (error) {
          logger.error("Error deleting document:", error);
          message.error("فشل في حذف المستند");
        }
      }
    });
  };

  // عرض المستند
  const handlePreviewDocument = (document) => {
    // استخدام نقطة النهاية الصحيحة للمعاينة بدلاً من الوصول المباشر إلى file_path
    const previewUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/documents/${document.id}/preview`;
    window.open(previewUrl, '_blank');
  };

  // الحصول على نوع المستند من امتداد الملف
  const getDocumentType = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    const types = {
      pdf: "PDF",
      doc: "Word",
      docx: "Word",
      jpg: "صورة",
      jpeg: "صورة",
      png: "صورة",
      zip: "مضغوط",
      rar: "مضغوط",
    };
    return types[extension] || "غير معروف";
  };

  return (
    <div className="document-upload-container">
      <div className="mb-4">
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">اسحب الملفات هنا أو انقر للرفع</p>
          <p className="ant-upload-hint">
            يمكنك رفع حتى {maxCount} ملفات. الأنواع المسموح بها: {acceptedTypes}
          </p>
        </Dragger>
      </div>

      {fileList.length > 0 && (
        <div className="mb-4">
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            icon={<UploadOutlined />}
            style={{ marginTop: 16 }}
          >
            {uploading ? "جاري الرفع..." : "رفع المستندات"}
          </Button>
        </div>
      )}

      {documents.length > 0 && (
        <div className="mt-4">
          <h4>المستندات الحالية:</h4>
          <List
            itemLayout="horizontal"
            dataSource={documents}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreviewDocument(item)}
                    title="عرض المستند"
                  />,
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDocument(item.id)}
                    title="حذف المستند"
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={<FileOutlined />}
                  title={
                    <div>
                      <span>{item.title || item.original_name}</span>
                      <Tag className="mr-2" color="blue">
                        {getDocumentType(item.original_name)}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <div>
                        تاريخ الرفع:{" "}
                        {new Date(item.created_at).toLocaleDateString("ar-IQ")}
                      </div>
                      {item.notes && <div>ملاحظات: {item.notes}</div>}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      <Modal
        title="معاينة المستند"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            إغلاق
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => {
              const downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/documents/${previewFile?.id}/download`;
              window.open(downloadUrl, "_blank");
            }}
          >
            تحميل
          </Button>,
        ]}
        width="80%"
      >
        {previewFile && (
          <div style={{ textAlign: "center" }}>
            <p>انقر على زر "تحميل" لتنزيل الملف أو "إغلاق" للعودة</p>
            <p>اسم الملف: {previewFile.title || previewFile.original_name}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentUpload;
